// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.

import {
  type AssetsClassifierResp,
  type AssetsInfoResp,
} from '@shared/data-access/interfaces/api/ai/assets';
import { groupPositionsByAssetType } from './group-positions-by-asset-type';

export const buildAssetsComparison = async (
  classifierResponse: AssetsClassifierResp[],
  getAssetsInfo: (
    assetType: string,
    tickers: string[],
  ) => Promise<AssetsInfoResp[]>,
  benchmark: string,
): Promise<AssetsInfoResp[]> => {
  const { other, ...assetTypes } = groupPositionsByAssetType(classifierResponse);

  const assetsInfoPromises = Object.entries(assetTypes).map(
    async ([assetType, assets]) => {
      if (assets.length === 0) return Promise.resolve([]);

      const tickers = assets.map(({ ticker }) => ticker);

      return getAssetsInfo(assetType, tickers).then(data =>
        data.map(item => ({ ...item, asset_type: assetType })),
      );
    },
  );

  const otherInfo = other.map(item => ({
    asset_class: item.asset_class,
    ticker: item.ticker,
    name: item.name,
    exchange: item.market,
    asset_type: 'other',
    avg_volume: 0,
    benchmark: '',
    category: '',
    currency: '-',
    description: '',
    isin: '',
    sector: '',
    dividend_yield: 0,
    pe: 0,
    pb: 0,
    etf_company: '',
    figi: '',
    investment_segment: '',
    ipo: '',
    last_update: '',
    manager: '',
    market_cap: 0,
    max_draw_down: 0,
    nav: 0,
    outstanding_balance: 0,
    status: 0,
    tracking_index: '',
    url_company: '',
    url_logo: '',
    url_morningstar: '',
    website: '',
    y10_return: 0,
    y10_vol: 0,
    y1_return: 0,
    y1_vol: 0,
    y3_return: 0,
    y3_vol: 0,
    y5_return: 0,
    y5_vol: 0,
    ytd_return: 0,
    inception_return: 0,
    m1_return: 0,
    m3_return: 0,
    m6_return: 0,
    price: null,
    price_as_of: null,
  }));

  const assetsInfoResults = await Promise.all(assetsInfoPromises);

  const assetsInfo = assetsInfoResults
    .flat()
    .concat(otherInfo)
    .sort((a, b) => (a.ticker === benchmark ? -1 : b.ticker === benchmark ? 1 : 0));

  return assetsInfo;
};
